import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = '/api/static/brand/logo-h0.png';

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const pickLang = (obj, lang) => (obj && (obj[lang] || obj.ca)) || '';

// Inline markdown: **bold** → <strong>, paragraphs separated by \n\n
const renderBody = (text) => {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/);
  const renderInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={i} className="text-[#F2E8D5]">{p.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  };
  return paragraphs.map((para, i) => (
    <p key={i} className={i > 0 ? 'mt-4' : ''}>{renderInline(para)}</p>
  ));
};

export const CommunityHelp = ({ community, brandLabel }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ca';
  const [help, setHelp] = useState(null);
  const [loading, setLoading] = useState(true);
  const dashboardPath = `/${community}`;

  useEffect(() => {
    axios.get(`${API_URL}/api/${community}/help`)
      .then((r) => setHelp(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [community]);

  if (loading || !help) {
    return (
      <div className="min-h-screen w-full bg-[#0E4D2A] flex items-center justify-center text-[#A8B4A6] font-sans tracking-widest text-sm">
        ...
      </div>
    );
  }

  const visibleSections = (help.sections || [])
    .filter((s) => s.active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden" data-testid={`${community}-help-page`}>
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span className="font-serif font-black text-[#083B1F]" style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.3 }}>
          H0
        </span>
      </div>

      {/* Top nav */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-5 md:px-10 py-5">
        <Link
          to={dashboardPath}
          data-testid={`${community}-help-back`}
          className="group flex items-center gap-2 font-sans text-[10px] md:text-xs tracking-[0.32em] text-[#A8B4A6] hover:text-[#D4AF37] transition-colors uppercase"
        >
          <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
          {brandLabel || community.toUpperCase()}
        </Link>
        <div className="hidden md:flex gap-4 font-sans text-[11px] tracking-[0.25em]">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => i18n.changeLanguage(l.code)}
              className={`transition-colors duration-300 ${
                lang === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-24">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center text-center"
        >
          <img src={LOGO_URL} alt="Hoyo Zero" className="w-16 sm:w-20 h-auto" />
          <p className="mt-6 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
            ❓ {community.toUpperCase()}
          </p>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight" data-testid={`${community}-help-title`}>
            {pickLang(help.intro_title, lang)}
          </h1>
          <p className="mt-5 font-serif italic text-[#A8B4A6] text-base md:text-lg max-w-2xl">
            {pickLang(help.intro_body, lang)}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="mt-16 space-y-10">
          {visibleSections.map((s, i) => (
            <motion.section
              key={s.key}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="border border-[#D4AF37]/15 bg-[#083B1F]/30 backdrop-blur-sm rounded-2xl p-6 md:p-8"
              data-testid={`${community}-help-section-${s.key}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{s.icon}</span>
                <h2 className="font-serif text-2xl md:text-3xl tracking-tight">
                  {pickLang(s.title, lang)}
                </h2>
              </div>
              <div className="font-sans font-light text-[#F2E8D5]/85 leading-relaxed text-sm md:text-base">
                {renderBody(pickLang(s.body, lang))}
              </div>
            </motion.section>
          ))}
        </div>

        <p className="mt-16 text-center text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/60 font-sans">
          © {new Date().getFullYear()} · Hoyo Zero · @joan_monzo
        </p>
      </div>
    </div>
  );
};

export default CommunityHelp;
